import { api } from '../../services/api'

/** Longer timeout for multipart (RC / Aadhaar images). Default api timeout is 60s. */
const MULTIPART_TIMEOUT_MS = 180000

const state = () => ({ items: [], loading: false, error: null })

const getters = {
  all: (s) => {
    console.log('[Getter] all → items:', s.items)
    return s.items
  }
}

const mutations = {
  SET_ITEMS(state, items) {
    console.log('[Mutation] SET_ITEMS → received:', items)
    state.items = items
    console.log('[Mutation] SET_ITEMS → state.items now:', state.items)
  },
  ADD_ITEM(state, item) {
    console.log('[Mutation] ADD_ITEM → new item:', item)
    state.items.unshift(item)
    console.log('[Mutation] ADD_ITEM → state.items now:', state.items)
  },
  UPDATE_ITEM(state, item) {
    console.log('[Mutation] UPDATE_ITEM → looking for id:', item.id)
    const i = state.items.findIndex(x => String(x.id) === String(item.id))
    console.log('[Mutation] UPDATE_ITEM → found at index:', i)
    if (i !== -1) {
      state.items.splice(i, 1, item)
      console.log('[Mutation] UPDATE_ITEM → updated item:', item)
    } else {
      console.warn('[Mutation] UPDATE_ITEM → item NOT found in state!')
    }
  },
  REMOVE_ITEM(state, id) {
    console.log('[Mutation] REMOVE_ITEM → removing id:', id)
    state.items = state.items.filter(x => String(x.id) !== String(id))
    console.log('[Mutation] REMOVE_ITEM → state.items now:', state.items)
  },
  SET_LOADING(state, v) {
    console.log('[Mutation] SET_LOADING →', v)
    state.loading = v
  },
  SET_ERROR(state, e) {
    console.log('[Mutation] SET_ERROR →', e)
    state.error = e
  }
}

const actions = {
  async list({ commit }) {
    console.log('[Action] list → starting...')
    commit('SET_LOADING', true)
    try {
      console.log('[Action] list → calling API: GET admin/purchase-records')
      const { data } = await api.get('admin/purchase-records')
      console.log('[Action] list → API response raw data:', data)

      const items = Array.isArray(data) ? data : (data?.items || [])
      console.log('[Action] list → parsed items:', items)

      commit('SET_ITEMS', items)
      commit('SET_ERROR', null)
      console.log('[Action] list → done ✅')
    } catch (e) {
      console.error('[Action] list → ERROR:', e)
      console.error('[Action] list → response:', e.response)
      console.error('[Action] list → response data:', e.response?.data)
      commit('SET_ERROR', e.message || 'Failed to load')
    } finally {
      commit('SET_LOADING', false)
      console.log('[Action] list → loading set to false')
    }
  },

  async listAvailable({ commit }) {
    console.log('[Action] listAvailable → starting...')
    try {
      console.log('[Action] listAvailable → calling API: GET admin/purchase-records/available')
      const { data } = await api.get('admin/purchase-records/available')
      console.log('[Action] listAvailable → API response raw data:', data)

      const items = Array.isArray(data) ? data : (data?.items || data || [])
      console.log('[Action] listAvailable → parsed items:', items)
      console.log('[Action] listAvailable → done ✅')
      return items
    } catch (e) {
      console.error('[Action] listAvailable → ERROR:', e)
      console.error('[Action] listAvailable → response:', e.response)
      console.error('[Action] listAvailable → response data:', e.response?.data)
      return []
    }
  },

  async create({ commit }, payload) {
    console.log('[Action] create → payload:', payload)
    if (payload instanceof FormData) {
      console.log('[Action] create → FormData entries:')
      for (let [key, value] of payload.entries()) {
        console.log(`   ${key}:`, value)
      }
    }
    try {
      console.log('[Action] create → calling API: POST admin/purchase-records')
      const cfg = payload instanceof FormData ? { timeout: MULTIPART_TIMEOUT_MS } : undefined
      const { data } = await api.post('admin/purchase-records', payload, cfg)
      console.log('[Action] create → API response:', data)
      commit('ADD_ITEM', data)
      console.log('[Action] create → done ✅')
      return data
    } catch (e) {
      console.error('[Action] create → ERROR:', e)
      console.error('[Action] create → response data:', e.response?.data)
      throw e
    }
  },

  async update({ commit }, { id, payload }) {
    console.log('[Action] update → id:', id)
    if (payload instanceof FormData) {
      let n = 0
      for (const _ of payload.entries()) n++
      console.log('[Action] update → FormData parts:', n, '(Chrome console often shows FormData as {} even when it has fields)')
    } else {
      console.log('[Action] update → payload:', payload)
    }

    const isFormData = payload instanceof FormData
    const postMultipart = () => {
      const fd = new FormData()
      if (isFormData) {
        for (const [key, value] of payload.entries()) {
          if (key === '_method') continue
          fd.append(key, value)
        }
      }
      fd.append('_method', 'PUT')
      console.log(`[Action] update → POST admin/purchase-records/${id} (multipart)`)
      return api.post(`admin/purchase-records/${id}`, fd, { timeout: MULTIPART_TIMEOUT_MS })
    }

    const runOnce = async () => {
      if (isFormData) {
        const { data } = await postMultipart()
        return data
      }
      console.log(`[Action] update → PUT admin/purchase-records/${id}`)
      const { data } = await api.put(`admin/purchase-records/${id}`, payload)
      return data
    }

    const isLockTimeout = (e) => {
      const msg = String(e?.response?.data?.error ?? e?.response?.data?.message ?? e?.message ?? '')
      if (/econnreset|etimedout|econnaborted|socket hang up|enotfound|enetunreach/i.test(msg)) return false
      return /lock wait timeout|try restarting transaction|deadlock/i.test(msg)
    }

    try {
      let data
      try {
        data = await runOnce()
      } catch (e) {
        if (isLockTimeout(e)) {
          console.warn('[Action] update → lock timeout, retrying once after delay…')
          await new Promise((r) => setTimeout(r, 2500))
          data = await runOnce()
        } else {
          throw e
        }
      }
      console.log('[Action] update → API response:', data)
      commit('UPDATE_ITEM', data)
      console.log('[Action] update → done ✅')
      return data
    } catch (e) {
      console.error('[Action] update → ERROR:', e)
      console.error('[Action] update → response data:', e.response?.data)
      throw e
    }
  },

  async remove({ commit }, id) {
    console.log('[Action] remove → id:', id)
    try {
      console.log(`[Action] remove → calling API: DELETE admin/purchase-records/${id}`)
      await api.delete(`admin/purchase-records/${id}`)
      console.log('[Action] remove → API call success')
      commit('REMOVE_ITEM', id)
      console.log('[Action] remove → done ✅')
    } catch (e) {
      console.error('[Action] remove → ERROR:', e)
      console.error('[Action] remove → response data:', e.response?.data)
      throw e
    }
  }
}

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions
}